import { useState } from "react";
import { z } from "zod";

const schema = z.object({
  nome: z.string().trim().min(2, "Inserisci il tuo nome").max(100, "Nome troppo lungo"),
  email: z.string().trim().email("Indirizzo email non valido").max(255),
  telefono: z.string().trim().max(30, "Numero troppo lungo").optional().or(z.literal("")),
  superficie: z.string().trim().max(80).optional().or(z.literal("")),
  messaggio: z
    .string()
    .trim()
    .min(10, "Descrivi brevemente il lavoro (almeno 10 caratteri)")
    .max(2000, "Messaggio troppo lungo"),
});

const superfici = [
  "Cotto antico",
  "Cotto moderno",
  "Marmo",
  "Pietra naturale",
  "Graniglia / seminato / cementine",
  "Grès porcellanato",
  "Altro / non so",
];

export function PreventivoForm() {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const parsed = schema.safeParse({
      nome: String(fd.get("nome") ?? ""),
      email: String(fd.get("email") ?? ""),
      telefono: String(fd.get("telefono") ?? ""),
      superficie: String(fd.get("superficie") ?? ""),
      messaggio: String(fd.get("messaggio") ?? ""),
    });

    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }

    setErrors({});
    setSending(true);
    const [{ toast }, { inviaRichiestaPreventivo }] = await Promise.all([
      import("sonner"),
      import("@/lib/preventivo.functions"),
    ]);

    try {
      await inviaRichiestaPreventivo({ data: parsed.data });
    } catch {
      setSending(false);
      toast.error("Invio non riuscito. Riprova o chiamaci direttamente.");
      return;
    }
    setSending(false);

    form.reset();
    setDone(true);
    toast.success("Richiesta inviata. Ti ricontattiamo al più presto.");
  };

  const field =
    "mt-1.5 w-full rounded-xs border border-border bg-background px-3 py-2.5 text-sm outline-hidden transition-colors focus:border-primary";
  const labelCls = "text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase";

  if (done) {
    return (
      <div className="surface-warm rounded-sm border border-border p-8 text-center">
        <h3 className="text-lg font-semibold">Grazie, abbiamo ricevuto la tua richiesta.</h3>
        <p className="mt-3 text-sm text-muted-foreground">
          Ti rispondiamo in giornata. Se hai fretta, chiamaci al +39 339 751 7813.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className="mt-6 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Invia un'altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="surface-warm rounded-sm border border-border p-7">
      <h3 className="text-lg font-semibold">Richiedi un preventivo gratuito</h3>
      <p className="mt-2 text-sm text-muted-foreground">
        Compila il modulo: sopralluogo e preventivo sono sempre gratuiti e senza impegno.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="nome">Nome e cognome</label>
          <input id="nome" name="nome" required maxLength={100} autoComplete="name" className={field} />
          {errors['nome'] && <p className="mt-1 text-xs text-destructive">{errors['nome']}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="telefono">Telefono</label>
          <input id="telefono" name="telefono" maxLength={30} autoComplete="tel" className={field} />
          {errors['telefono'] && <p className="mt-1 text-xs text-destructive">{errors['telefono']}</p>}
        </div>
      </div>

      <div className="mt-4">
        <label className={labelCls} htmlFor="email">Email</label>
        <input id="email" name="email" type="email" required maxLength={255} autoComplete="email" className={field} />
        {errors['email'] && <p className="mt-1 text-xs text-destructive">{errors['email']}</p>}
      </div>

      <div className="mt-4">
        <label className={labelCls} htmlFor="superficie">Tipo di superficie</label>
        <select id="superficie" name="superficie" defaultValue="" className={field}>
          <option value="">Seleziona…</option>
          {superfici.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <label className={labelCls} htmlFor="messaggio">Descrivi il lavoro</label>
        <textarea
          id="messaggio"
          name="messaggio"
          required
          rows={5}
          maxLength={2000}
          placeholder="Metri quadri indicativi, comune, stato del pavimento…"
          className={field}
        />
        {errors['messaggio'] && <p className="mt-1 text-xs text-destructive">{errors['messaggio']}</p>}
      </div>

      <button
        type="submit"
        disabled={sending}
        className="mt-6 w-full rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna disabled:opacity-60"
      >
        {sending ? "Invio in corso…" : "Invia la richiesta"}
      </button>
    </form>
  );
}
